import { Form } from '@ant-design/compatible';
import ErrorHandler from 'common/error/components/error-handler';
import SidePanel from 'common/side-panel/side-panel';
import Spinner from 'common/spinner/components/spinner';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { compose } from 'redux';
import styled from 'styled-components';
import { Div, Icon, notification, Span } from 'ui';
import { withRouter } from 'utils/withRouter';

import {
  orderStatus,
  partnerTypes,
  partnerTypesToSearch,
} from '../../../utils/app-configs';
import AdvancedChangePartnerForm from './advanced-change-partner-form';
import Details from './details';
import SimpleChangePartnerForm from './simple-change-partner-form';

const Divider = styled(Div)`
  border-top: 1px solid #dce4ea;
  margin: 10px 0;
`;

class ChangePartner extends Component {
  static propTypes = {
    data: PropTypes.array,
    isLoading: PropTypes.bool,
    error: PropTypes.object,
    isSaving: PropTypes.bool,
    saveSuccess: PropTypes.bool,
    getPartnersRequest: PropTypes.func,
    savePartnerRequest: PropTypes.func,
    setFormDirty: PropTypes.func,
    setSidepanelClosed: PropTypes.func,

    order: PropTypes.object,
    objectType: PropTypes.string,
    isDetailLoading: PropTypes.bool,
    addEditedPartner: PropTypes.func,
    history: PropTypes.object,
    match: PropTypes.object,
    intl: PropTypes.object,
    form: PropTypes.object,
    editedPartners: PropTypes.array,
    engineer: PropTypes.string,
    preferredEngineer: PropTypes.string,
    lastVisitEngineer: PropTypes.string,
    searchData: PropTypes.array,
    savePartnerDetailsRequest: PropTypes.func,
    addPartnerDetailsRequest: PropTypes.func,
    lastUsed: PropTypes.array,
    saveDetailsSuccess: PropTypes.bool,
  };

  state = { selectedPartner: null, isShipTo: false, canSearch: false };

  componentWillMount() {
    const { match: { params: { name } } } = this.props;
    if (name === 'SHIP-TO') {
      this.setState({ isShipTo: true });
    }
  }

  componentDidMount() {
    const { getPartnersRequest, match: { params: { type } }, order } = this.props;
    if (this.state.isShipTo) {
      getPartnersRequest({ entityId: order.id, query: 'shipToEdit' });
    } else {
      getPartnersRequest({ type, entityId: order.id });
    }
  }

  componentWillReceiveProps(nextProps) {
    if (partnerTypesToSearch.includes(nextProps.match.params.type)) {
      this.setState({
        canSearch: true,
      });
    }
    // TODO: check if this is not an anti-pattern
    if (nextProps.saveSuccess && !this.props.saveSuccess && !nextProps.isSaving) {
      notification.success({
        message: nextProps.intl.formatMessage({ id: 'partners.SAVE_SUCCESS' }),
        placement: 'top',
      });
      nextProps.setFormDirty();
      this.gotoDetails();
    }
    // When de data is updated, update the selectedpartner info
    if (
      !this.props.saveDetailsSuccess &&
      nextProps.saveDetailsSuccess
    ) {
      if (this.state.selectedPartner) {
        if (nextProps?.lastUsed?.length) {
          // Always select the first entry of the array because it is the most recent one
          const [newPartner] = nextProps.lastUsed;

          this.setState({
            selectedPartner: {
              ...newPartner,
              id: newPartner.cosmosId,
              isLastUsed: true,
            },
          });
          nextProps.addEditedPartner(newPartner.cosmosId);
        } else {
          this.setState({
            selectedPartner: nextProps.data?.find(i => i.id === this.state.selectedPartner?.id),
          });
          nextProps.addEditedPartner(this.state.selectedPartner?.id);
        }
      } else {
        const [newPartner] = nextProps.data?.filter(d =>
          !this.props.data?.find(p => p.id === d.id),
        );
        if (newPartner) {
          this.setState({
            selectedPartner: newPartner,
          });
          nextProps.addEditedPartner(newPartner?.id);
        }
      }
    }
  }

  handleSave = (e) => {
    e.preventDefault();
    const { savePartnerRequest, form, order, match, lastUsed, editedPartners } = this.props;
    const { isShipTo, canSearch, selectedPartner } = this.state;
    const removePartnerId = match.params.id;
    form.validateFields((err, values) => {
      let addPartnerId = canSearch ? selectedPartner?.id : values.partner;
      if (selectedPartner?.isLastUsed) {
        addPartnerId = lastUsed?.find(p => p.cosmosId === values?.partner)?.id;
      }
      if (values.partner || selectedPartner?.id) {
        const id = selectedPartner.isLastUsed ? selectedPartner.cosmosId : values.partner;
        savePartnerRequest({
          order,
          addPartnerId,
          removePartnerId,
          isShipTo,
          cosmosId: selectedPartner.cosmosId,
          isFlexible: !!editedPartners?.find(p =>
            p === id,
          ),
        });
      }
    });
  };

  handleSelect = (value, option) => {
    const { searchData, data, lastUsed } = this.props;
    const { canSearch } = this.state;
    let dropdownData = data;
    if (canSearch) {
      dropdownData = searchData;
    }
    let selectedPartner = dropdownData?.find(p => p.id === value || p.number === value);
    if (option?.isLastUsed) {
      selectedPartner = {
        ...lastUsed?.find(p => p.cosmosId === value),
        isLastUsed: true,
      };
    }
    this.setState(
      {
        selectedPartner,
      },
      () => this.props.form.validateFields(),
    );
  };

  handleClear = () => {
    this.setState(
      {
        selectedPartner: null,
      },
    );
  };

  gotoDetails = () => {
    this.props.history.back();
    this.props.setSidepanelClosed();
  };

  renderLine = (line, icon) => {
    if (line) {
      if (icon) {
        return (
          <Span>
            <Icon type={icon} marginRight />
            <Span dangerouslySetInnerHTML={{ __html: line }} />
          </Span>
        );
      }
      return <Span dangerouslySetInnerHTML={{ __html: line }} />;
    }
    return null;
  };

  render() {
    const {
      isLoading,
      error,
      order,
      objectType,
      isDetailLoading,
      form: { getFieldDecorator },
      intl: { formatMessage },
      match: { params: { name, isNew, type } },
      savePartnerDetailsRequest,
      addPartnerDetailsRequest,
      intl,
    } = this.props;
    const { selectedPartner, canSearch } = this.state;

    const partnerValidator = getFieldDecorator('partner', {
      rules: [{ required: true, message: intl.formatMessage({ id: 'rules.PARTNER' }) }],
    });
    const typeName = name?.replace('-', ' ');
    return (
      <SidePanel
        title={
          isNew === 'true'
            // eslint-disable-next-line max-len
            ? `${formatMessage({ id: 'partners.TITLE_NEW' })} ${typeName.toUpperCase()}`
            // eslint-disable-next-line max-len
            : `${formatMessage({ id: 'partners.TITLE_CHANGE' })} ${typeName.toUpperCase()}`
        }
        width='45%'
        onPanelClose={this.gotoDetails}
      >
        <ErrorHandler type='notification' error={error} />
        {isDetailLoading && isLoading ?
          <Spinner title={<FormattedMessage id='partners.LOADING_DETAILS' />} />
          :
          <>
            <Details data={order} type={objectType} />
            <Divider />
            <Form onSubmit={this.handleSave} layout='vertical'>
              {type === partnerTypes.SHIPTO ||
                type === partnerTypes.INSTALLEDAT ||
                type === partnerTypes.CONTACTPERSON ?
                // eslint-disable-next-line
                <AdvancedChangePartnerForm
                  partnerValidator={partnerValidator}
                  canSearch={canSearch}
                  type={type}
                  renderLine={this.renderLine}
                  selectedPartner={selectedPartner}
                  handleSelect={this.handleSelect}
                  gotoDetails={this.gotoDetails}
                  typeName={name}
                  savePartnerDetailsRequest={savePartnerDetailsRequest}
                  addPartnerDetailsRequest={addPartnerDetailsRequest}
                  canEditPartner={order?.statusInfo?.id <= orderStatus.released}
                  entityId={order?.id}
                  showEditPartnerRegion={order?.showEditPartnerRegion}
                  {...this.props}
                /> :
                <SimpleChangePartnerForm
                  partnerValidator={partnerValidator}
                  canSearch={canSearch}
                  scopeWide={type === partnerTypes.EMPLOYEE}
                  type={type}
                  renderLine={this.renderLine}
                  selectedPartner={selectedPartner}
                  handleSelect={this.handleSelect}
                  gotoDetails={this.gotoDetails}
                  name={name}
                  handleClear={this.handleClear}
                  {...this.props}
                />
              }
            </Form>
          </>
        }
      </SidePanel>
    );
  }
}

export default compose(withRouter, Form.create(), injectIntl)(ChangePartner);
